import defaultsDeep from 'lodash.defaultsdeep';
import PropTypes from 'prop-types';
import React from 'react';

import VMScratchBlocks from '../../lib/blocks';
import {DEFAULT_MODE, getColorsForMode} from '../../lib/settings/color-mode';

import styles from './stage-3d.css';

const EMPTY_WORKSPACE_XML = '<xml xmlns="http://www.w3.org/1999/xhtml"></xml>';
const workspaceXmlHasBlocks = workspaceXml => /<block[\s>]/.test(String(workspaceXml || ''));

const NUMBER_SHADOW = (name, value) => (
    `<value name="${name}"><shadow type="math_number"><field name="NUM">${value}</field></shadow></value>`
);

const DEFAULT_OBJECT_MENU_OPTIONS = [
    ['任意对象', '*'],
    ['自己', 'self']
];

const DIRECTION_MENU_OPTIONS = [
    ['右上', 'rightUp'],
    ['左上', 'leftUp'],
    ['右下', 'rightDown'],
    ['左下', 'leftDown'],
    ['上', 'up'],
    ['下', 'down']
];

const AXIS_MENU_OPTIONS = [
    ['X', 'x'],
    ['Y', 'y'],
    ['Z', 'z']
];

const STATE_MENU_OPTIONS = [
    ['普通', 'normal'],
    ['打开', 'open'],
    ['关闭', 'closed'],
    ['锁住', 'locked'],
    ['启动', 'active'],
    ['停止', 'inactive'],
    ['已收集', 'collected']
];

const ANIMATION_MENU_OPTIONS = [
    ['默认', 'idle'],
    ['行走', 'walk'],
    ['跳起', 'jump'],
    ['摇晃', 'sway'],
    ['旋转', 'spin'],
    ['闪烁', 'blink'],
    ['生长', 'grow'],
    ['打开', 'open'],
    ['关闭', 'close']
];

const WEATHER_MENU_OPTIONS = [
    ['晴朗', 'sunny'],
    ['雨天', 'rainy'],
    ['阴天', 'cloudy'],
    ['大风', 'windy']
];

const WEATHER_PROPERTY_MENU_OPTIONS = [
    ['天气', 'type'],
    ['气温', 'temperature'],
    ['风力', 'windForce'],
    ['光照', 'lighting'],
    ['紫外线', 'ultraviolet']
];

const TERRAIN_MENU_OPTIONS = [
    ['草地方块', 'grass-block'],
    ['泥土方块', 'dirt-block'],
    ['石头方块', 'stone-block'],
    ['沙地方块', 'sand-block'],
    ['水面方块', 'water-block'],
    ['木板方块', 'wood-block']
];

const COLLISION_MENU_OPTIONS = [
    ['可穿透', 'passThrough'],
    ['实体阻挡', 'solid'],
    ['触发器', 'trigger'],
    ['仅装饰', 'decoration']
];

const COMPARE_MENU_OPTIONS = [
    ['=', 'eq'],
    ['不等于', 'ne'],
    ['<', 'lt'],
    ['<=', 'lte'],
    ['>', 'gt'],
    ['>=', 'gte']
];

const MATH_MENU_OPTIONS = [
    ['+', 'add'],
    ['-', 'subtract'],
    ['*', 'multiply'],
    ['/', 'divide']
];

const VARIABLE_MENU_OPTIONS = [
    ['分数', 'score'],
    ['能量', 'energy'],
    ['数量', 'count'],
    ['开关', 'flag']
];

const MESSAGE_MENU_OPTIONS = [
    ['开始任务', 'startQuest'],
    ['打开机关', 'openSwitch'],
    ['收集完成', 'collected'],
    ['通关成功', 'levelClear']
];

const INSERT_SNAP_RADIUS = 48;

let scratch3DObjectMenuOptions = DEFAULT_OBJECT_MENU_OPTIONS;
let scratch3DVariableMenuOptions = VARIABLE_MENU_OPTIONS;
let scratch3DMessageMenuOptions = MESSAGE_MENU_OPTIONS;

const normalizeMenuOptions = (options, fallbackOptions) => {
    const normalized = (Array.isArray(options) ? options : [])
        .filter(option => Array.isArray(option) && option.length >= 2)
        .map(option => [String(option[0]), String(option[1])]);
    return normalized.length ? normalized : fallbackOptions;
};

const normalizeObjectMenuOptions = options => normalizeMenuOptions(options, DEFAULT_OBJECT_MENU_OPTIONS);

export const setScratch3DObjectMenuOptions = options => {
    scratch3DObjectMenuOptions = normalizeObjectMenuOptions(options);
};

export const setScratch3DVariableMenuOptions = options => {
    scratch3DVariableMenuOptions = normalizeMenuOptions(options, VARIABLE_MENU_OPTIONS);
};

export const setScratch3DMessageMenuOptions = options => {
    scratch3DMessageMenuOptions = normalizeMenuOptions(options, MESSAGE_MENU_OPTIONS);
};

const getScratch3DObjectMenuOptions = () => scratch3DObjectMenuOptions;
const getScratch3DVariableMenuOptions = () => scratch3DVariableMenuOptions;
const getScratch3DMessageMenuOptions = () => scratch3DMessageMenuOptions;

const ensureScratch3DContextMenuMessages = ScratchBlocks => {
    if (!ScratchBlocks || !ScratchBlocks.Msg) return;

    ScratchBlocks.Msg.DELETE_BLOCK = ScratchBlocks.Msg.DELETE_BLOCK || '删除积木';
    ScratchBlocks.Msg.DELETE_X_BLOCKS = ScratchBlocks.Msg.DELETE_X_BLOCKS || '删除 %1 个积木';
    ScratchBlocks.Msg.DELETE_ALL_BLOCKS = ScratchBlocks.Msg.DELETE_ALL_BLOCKS || '删除全部 %1 个积木？';
};

const THREE_D_TOOLBOX_XML = `
<xml id="scratch3d-toolbox" style="display: none">
    <category name="事件" id="scratch3dEvents" colour="#ffbf00" secondaryColour="#cc9900">
        <block type="scratch3d_event_when_start" />
        <block type="scratch3d_event_when_click_self" />
        <block type="scratch3d_event_when_touching_object" />
        <block type="scratch3d_event_when_receive_message" />
        <block type="scratch3d_event_broadcast_message" />
        <block type="scratch3d_event_send_message_to_object" />
    </category>
    <category name="动作" id="scratch3dMotion" colour="#0fbd8c" secondaryColour="#0b8e69">
        <block type="scratch3d_motion_face_direction" />
        <block type="scratch3d_motion_face_object" />
        <block type="scratch3d_motion_move_forward">
            ${NUMBER_SHADOW('STEPS', 1)}
        </block>
        <block type="scratch3d_motion_move_grid">
            ${NUMBER_SHADOW('STEPS', 1)}
        </block>
        <block type="scratch3d_motion_move_to_object" />
        <block type="scratch3d_motion_move_toward_object">
            ${NUMBER_SHADOW('STEPS', 1)}
        </block>
        <block type="scratch3d_motion_move_away_object">
            ${NUMBER_SHADOW('STEPS', 1)}
        </block>
        <block type="scratch3d_motion_move_to">
            ${NUMBER_SHADOW('X', 0)}
            ${NUMBER_SHADOW('Y', 0)}
            ${NUMBER_SHADOW('Z', 0)}
        </block>
        <block type="scratch3d_motion_change_position">
            ${NUMBER_SHADOW('VALUE', 1)}
        </block>
        <block type="scratch3d_motion_set_position_axis">
            ${NUMBER_SHADOW('VALUE', 0)}
        </block>
        <block type="scratch3d_motion_change_z">
            ${NUMBER_SHADOW('Z', 1)}
        </block>
        <block type="scratch3d_motion_turn_axis">
            ${NUMBER_SHADOW('DEGREES', 15)}
        </block>
        <block type="scratch3d_motion_set_rotation_axis">
            ${NUMBER_SHADOW('DEGREES', 0)}
        </block>
        <block type="scratch3d_motion_jump">
            ${NUMBER_SHADOW('HEIGHT', 1)}
        </block>
        <block type="scratch3d_motion_snap_to_surface" />
    </category>
    <category name="控制" id="scratch3dControl" colour="#ffab19" secondaryColour="#cf8b17">
        <block type="scratch3d_control_wait">
            ${NUMBER_SHADOW('SECONDS', 1)}
        </block>
        <block type="scratch3d_control_wait_until">
            <value name="CONDITION"><block type="scratch3d_sensing_front_blocked" /></value>
        </block>
        <block type="scratch3d_control_repeat">
            ${NUMBER_SHADOW('TIMES', 10)}
        </block>
        <block type="scratch3d_control_repeat_until">
            <value name="CONDITION"><block type="scratch3d_sensing_front_blocked" /></value>
        </block>
        <block type="scratch3d_control_if">
            <value name="CONDITION"><block type="scratch3d_sensing_front_blocked" /></value>
        </block>
        <block type="scratch3d_control_if_else">
            <value name="CONDITION"><block type="scratch3d_sensing_front_blocked" /></value>
        </block>
        <block type="scratch3d_control_forever" />
        <block type="scratch3d_control_stop_script" />
    </category>
    <category name="外观" id="scratch3dLooks" colour="#4E97FF" secondaryColour="#4E97FF">
        <block type="scratch3d_looks_play_animation" />
        <block type="scratch3d_looks_wait_animation_done" />
        <block type="scratch3d_looks_stop_animation" />
        <block type="scratch3d_looks_set_state" />
        <block type="scratch3d_looks_set_collision" />
        <block type="scratch3d_looks_set_size">
            ${NUMBER_SHADOW('SIZE', 100)}
        </block>
        <block type="scratch3d_looks_change_size">
            ${NUMBER_SHADOW('CHANGE', 10)}
        </block>
        <block type="scratch3d_looks_size_value" />
        <block type="scratch3d_looks_show" />
        <block type="scratch3d_looks_hide" />
    </category>
    <category name="侦测" id="scratch3dSensing" colour="#5cb1d6" secondaryColour="#2e8eb8">
        <block type="scratch3d_sensing_touching_object" />
        <block type="scratch3d_sensing_front_blocked" />
        <block type="scratch3d_sensing_distance_less_than">
            ${NUMBER_SHADOW('DISTANCE', 1)}
        </block>
        <block type="scratch3d_sensing_distance_to" />
        <block type="scratch3d_sensing_position_value" />
        <block type="scratch3d_sensing_rotation_value" />
        <block type="scratch3d_sensing_weather_value" />
        <block type="scratch3d_sensing_weather_property" />
        <block type="scratch3d_sensing_terrain_under" />
        <block type="scratch3d_sensing_surface_z" />
        <block type="scratch3d_sensing_state_value" />
        <block type="scratch3d_sensing_collision_mode" />
        <block type="scratch3d_sensing_visible_is" />
        <block type="scratch3d_sensing_object_visible_is" />
        <block type="scratch3d_sensing_state_is" />
        <block type="scratch3d_sensing_object_state_is" />
        <block type="scratch3d_sensing_collision_is" />
        <block type="scratch3d_sensing_object_collision_is" />
        <block type="scratch3d_sensing_terrain_under_is" />
        <block type="scratch3d_sensing_front_terrain_is" />
        <block type="scratch3d_sensing_weather_is" />
    </category>
    <category name="运算" id="scratch3dOperators" colour="#59c059" secondaryColour="#389438">
        <block type="scratch3d_operator_compare">
            ${NUMBER_SHADOW('LEFT', 0)}
            ${NUMBER_SHADOW('RIGHT', 0)}
        </block>
        <block type="scratch3d_operator_and" />
        <block type="scratch3d_operator_or" />
        <block type="scratch3d_operator_not" />
        <block type="scratch3d_operator_math">
            ${NUMBER_SHADOW('LEFT', 0)}
            ${NUMBER_SHADOW('RIGHT', 0)}
        </block>
    </category>
    <category name="变量" id="scratch3dVariables" colour="#ff8c1a" secondaryColour="#db6e00">
        <block type="scratch3d_variable_value" />
        <block type="scratch3d_variable_set">
            ${NUMBER_SHADOW('VALUE', 0)}
        </block>
        <block type="scratch3d_variable_change">
            ${NUMBER_SHADOW('VALUE', 1)}
        </block>
    </category>
    <category name="场景" id="scratch3dScene" colour="#59c059" secondaryColour="#389438">
        <block type="scratch3d_scene_set_weather" />
    </category>
</xml>
`;

export const THREE_D_BLOCK_PALETTE = [
    {
        id: 'events',
        label: '事件',
        blockClassName: 'eventBlock',
        blocks: [
            {
                label: '当开始运行',
                shapeClassName: 'hatBlock',
                parts: [{text: '当开始运行'}],
                xml: '<block type="scratch3d_event_when_start" />'
            },
            {
                label: '当点击自己',
                shapeClassName: 'hatBlock',
                parts: [{text: '当点击自己'}],
                xml: '<block type="scratch3d_event_when_click_self" />'
            },
            {
                label: '当碰到对象',
                shapeClassName: 'hatBlock',
                parts: [
                    {text: '当碰到'},
                    {value: '任意对象', dropdown: true}
                ],
                xml: '<block type="scratch3d_event_when_touching_object" />'
            }
        ]
    },
    {
        id: 'messages',
        label: '消息',
        blockClassName: 'eventBlock',
        blocks: [
            {
                label: '当收到消息 开始任务',
                shapeClassName: 'hatBlock',
                parts: [
                    {text: '当收到消息'},
                    {value: '开始任务', dropdown: true}
                ],
                xml: '<block type="scratch3d_event_when_receive_message" />'
            },
            {
                label: '广播消息 开始任务',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '广播消息'},
                    {value: '开始任务', dropdown: true}
                ],
                xml: '<block type="scratch3d_event_broadcast_message" />'
            },
            {
                label: '发送消息 开始任务 给对象',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '发送消息'},
                    {value: '开始任务', dropdown: true},
                    {text: '给'},
                    {value: '任意对象', dropdown: true}
                ],
                xml: '<block type="scratch3d_event_send_message_to_object" />'
            }
        ]
    },
    {
        id: 'motion',
        label: '动作',
        blockClassName: 'motionBlock',
        blocks: [
            {
                label: '面向 右上',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '面向'},
                    {value: '右上', dropdown: true}
                ],
                xml: '<block type="scratch3d_motion_face_direction" />'
            },
            {
                label: '面向对象 任意对象',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '面向对象'},
                    {value: '任意对象', dropdown: true}
                ],
                xml: '<block type="scratch3d_motion_face_object" />'
            },
            {
                label: '向前移动 1 格',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '向前移动'},
                    {value: '1'},
                    {text: '格'}
                ],
                xml: `<block type="scratch3d_motion_move_forward">${NUMBER_SHADOW('STEPS', 1)}</block>`
            },
            {
                label: '让自己向右上移动 1 格',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '让自己向'},
                    {value: '右上', dropdown: true},
                    {text: '移动'},
                    {value: '1'},
                    {text: '格'}
                ],
                xml: `<block type="scratch3d_motion_move_grid">${NUMBER_SHADOW('STEPS', 1)}</block>`
            },
            {
                label: '移动到对象位置 任意对象',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '移动到对象位置'},
                    {value: '任意对象', dropdown: true}
                ],
                xml: '<block type="scratch3d_motion_move_to_object" />'
            },
            {
                label: '靠近对象 任意对象 1 格',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '靠近对象'},
                    {value: '任意对象', dropdown: true},
                    {value: '1'},
                    {text: '格'}
                ],
                xml: `<block type="scratch3d_motion_move_toward_object">${NUMBER_SHADOW('STEPS', 1)}</block>`
            },
            {
                label: '远离对象 任意对象 1 格',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '远离对象'},
                    {value: '任意对象', dropdown: true},
                    {value: '1'},
                    {text: '格'}
                ],
                xml: `<block type="scratch3d_motion_move_away_object">${NUMBER_SHADOW('STEPS', 1)}</block>`
            },
            {
                label: '移动到格子 x 0 y 0 z 0',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '移动到格子 x'},
                    {value: '0'},
                    {text: 'y'},
                    {value: '0'},
                    {text: 'z'},
                    {value: '0'}
                ],
                xml: `<block type="scratch3d_motion_move_to">
                    ${NUMBER_SHADOW('X', 0)}
                    ${NUMBER_SHADOW('Y', 0)}
                    ${NUMBER_SHADOW('Z', 0)}
                </block>`
            },
            {
                label: '将 X 坐标增加 1',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '将'},
                    {value: 'X', dropdown: true},
                    {text: '坐标增加'},
                    {value: '1'}
                ],
                xml: `<block type="scratch3d_motion_change_position">${NUMBER_SHADOW('VALUE', 1)}</block>`
            },
            {
                label: '将 X 坐标设为 0',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '将'},
                    {value: 'X', dropdown: true},
                    {text: '坐标设为'},
                    {value: '0'}
                ],
                xml: `<block type="scratch3d_motion_set_position_axis">${NUMBER_SHADOW('VALUE', 0)}</block>`
            },
            {
                label: '将高度增加 1',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '将高度增加'},
                    {value: '1'}
                ],
                xml: `<block type="scratch3d_motion_change_z">${NUMBER_SHADOW('Z', 1)}</block>`
            },
            {
                label: '绕 Z 轴旋转 15 度',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '绕'},
                    {value: 'Z', dropdown: true},
                    {text: '轴旋转'},
                    {value: '15'},
                    {text: '度'}
                ],
                xml: `<block type="scratch3d_motion_turn_axis">${NUMBER_SHADOW('DEGREES', 15)}</block>`
            },
            {
                label: '将 Z 轴角度设为 0',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '将'},
                    {value: 'Z', dropdown: true},
                    {text: '轴角度设为'},
                    {value: '0'}
                ],
                xml: `<block type="scratch3d_motion_set_rotation_axis">${NUMBER_SHADOW('DEGREES', 0)}</block>`
            },
            {
                label: '让自己跳起 1 格',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '让自己跳起'},
                    {value: '1'},
                    {text: '格'}
                ],
                xml: `<block type="scratch3d_motion_jump">${NUMBER_SHADOW('HEIGHT', 1)}</block>`
            },
            {
                label: '贴合当前地面',
                shapeClassName: 'statementBlock',
                parts: [{text: '贴合当前地面'}],
                xml: '<block type="scratch3d_motion_snap_to_surface" />'
            }
        ]
    },
    {
        id: 'control',
        label: '控制',
        blockClassName: 'controlBlock',
        blocks: [
            {
                label: '等待 1 秒',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '等待'},
                    {value: '1'},
                    {text: '秒'}
                ],
                xml: `<block type="scratch3d_control_wait">${NUMBER_SHADOW('SECONDS', 1)}</block>`
            },
            {
                label: '等待直到 前方有障碍 ?',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '等待直到'},
                    {value: '前方有障碍 ?'}
                ],
                xml: '<block type="scratch3d_control_wait_until"><value name="CONDITION"><block type="scratch3d_sensing_front_blocked" /></value></block>'
            },
            {
                label: '重复执行 10 次',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '重复执行'},
                    {value: '10'},
                    {text: '次'}
                ],
                xml: `<block type="scratch3d_control_repeat">${NUMBER_SHADOW('TIMES', 10)}</block>`
            },
            {
                label: '重复直到 前方有障碍 ?',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '重复直到'},
                    {value: '前方有障碍 ?'}
                ],
                xml: '<block type="scratch3d_control_repeat_until"><value name="CONDITION"><block type="scratch3d_sensing_front_blocked" /></value></block>'
            },
            {
                label: '如果 前方有障碍 ? 那么',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '如果'},
                    {value: '前方有障碍 ?'},
                    {text: '那么'}
                ],
                xml: '<block type="scratch3d_control_if"><value name="CONDITION"><block type="scratch3d_sensing_front_blocked" /></value></block>'
            },
            {
                label: '如果 前方有障碍 ? 那么 否则',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '如果'},
                    {value: '前方有障碍 ?'},
                    {text: '那么 否则'}
                ],
                xml: '<block type="scratch3d_control_if_else"><value name="CONDITION"><block type="scratch3d_sensing_front_blocked" /></value></block>'
            },
            {
                label: '一直重复',
                shapeClassName: 'statementBlock',
                parts: [{text: '一直重复'}],
                xml: '<block type="scratch3d_control_forever" />'
            },
            {
                label: '停止此脚本',
                shapeClassName: 'statementBlock',
                parts: [{text: '停止此脚本'}],
                xml: '<block type="scratch3d_control_stop_script" />'
            }
        ]
    },
    {
        id: 'looks',
        label: '外观',
        blockClassName: 'looksBlock',
        blocks: [
            {
                label: '播放动画 默认',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '播放动画'},
                    {value: '默认', dropdown: true}
                ],
                xml: '<block type="scratch3d_looks_play_animation" />'
            },
            {
                label: '等待动画播放完成',
                shapeClassName: 'statementBlock',
                parts: [{text: '等待动画播放完成'}],
                xml: '<block type="scratch3d_looks_wait_animation_done" />'
            },
            {
                label: '停止动画',
                shapeClassName: 'statementBlock',
                parts: [{text: '停止动画'}],
                xml: '<block type="scratch3d_looks_stop_animation" />'
            },
            {
                label: '将自己的状态设为 普通',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '将自己的状态设为'},
                    {value: '普通', dropdown: true}
                ],
                xml: '<block type="scratch3d_looks_set_state" />'
            },
            {
                label: '将碰撞设为 实体阻挡',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '将碰撞设为'},
                    {value: '实体阻挡', dropdown: true}
                ],
                xml: '<block type="scratch3d_looks_set_collision" />'
            },
            {
                label: '将大小设为 100 %',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '将大小设为'},
                    {value: '100'},
                    {text: '%'}
                ],
                xml: `<block type="scratch3d_looks_set_size">${NUMBER_SHADOW('SIZE', 100)}</block>`
            },
            {
                label: '将大小增加 10',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '将大小增加'},
                    {value: '10'}
                ],
                xml: `<block type="scratch3d_looks_change_size">${NUMBER_SHADOW('CHANGE', 10)}</block>`
            },
            {
                label: '大小',
                shapeClassName: 'reporterBlock',
                parts: [{text: '大小'}],
                xml: '<block type="scratch3d_looks_size_value" />'
            },
            {
                label: '显示自己',
                shapeClassName: 'statementBlock',
                parts: [{text: '显示自己'}],
                xml: '<block type="scratch3d_looks_show" />'
            },
            {
                label: '隐藏自己',
                shapeClassName: 'statementBlock',
                parts: [{text: '隐藏自己'}],
                xml: '<block type="scratch3d_looks_hide" />'
            }
        ]
    },
    {
        id: 'sensing',
        label: '侦测',
        blockClassName: 'sensingBlock',
        blocks: [
            {
                label: '碰到对象 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '碰到对象'},
                    {value: '任意对象', dropdown: true},
                    {text: '?'}
                ],
                xml: '<block type="scratch3d_sensing_touching_object" />'
            },
            {
                label: '前方有障碍 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '前方'},
                    {value: '右上', dropdown: true},
                    {text: '有障碍 ?'}
                ],
                xml: '<block type="scratch3d_sensing_front_blocked" />'
            },
            {
                label: '到对象的距离小于 1 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '到对象'},
                    {value: '任意对象', dropdown: true},
                    {text: '的距离 <'},
                    {value: '1'},
                    {text: '?'}
                ],
                xml: `<block type="scratch3d_sensing_distance_less_than">${NUMBER_SHADOW('DISTANCE', 1)}</block>`
            },
            {
                label: '到对象的距离',
                shapeClassName: 'reporterBlock',
                parts: [
                    {text: '到对象'},
                    {value: '任意对象', dropdown: true},
                    {text: '的距离'}
                ],
                xml: '<block type="scratch3d_sensing_distance_to" />'
            },
            {
                label: '任意对象的 X 坐标',
                shapeClassName: 'reporterBlock',
                parts: [
                    {value: '任意对象', dropdown: true},
                    {text: '的'},
                    {value: 'X', dropdown: true},
                    {text: '坐标'}
                ],
                xml: '<block type="scratch3d_sensing_position_value" />'
            },
            {
                label: '任意对象的 Z 轴角度',
                shapeClassName: 'reporterBlock',
                parts: [
                    {value: '任意对象', dropdown: true},
                    {text: '的'},
                    {value: 'Z', dropdown: true},
                    {text: '轴角度'}
                ],
                xml: '<block type="scratch3d_sensing_rotation_value" />'
            },
            {
                label: '当前天气',
                shapeClassName: 'reporterBlock',
                parts: [{text: '当前天气'}],
                xml: '<block type="scratch3d_sensing_weather_value" />'
            },
            {
                label: '天气属性 气温',
                shapeClassName: 'reporterBlock',
                parts: [
                    {text: '天气属性'},
                    {value: '气温', dropdown: true}
                ],
                xml: '<block type="scratch3d_sensing_weather_property" />'
            },
            {
                label: '脚下地形',
                shapeClassName: 'reporterBlock',
                parts: [{text: '脚下地形'}],
                xml: '<block type="scratch3d_sensing_terrain_under" />'
            },
            {
                label: '当前地面高度',
                shapeClassName: 'reporterBlock',
                parts: [{text: '当前地面高度'}],
                xml: '<block type="scratch3d_sensing_surface_z" />'
            },
            {
                label: '自己的状态',
                shapeClassName: 'reporterBlock',
                parts: [{text: '自己的状态'}],
                xml: '<block type="scratch3d_sensing_state_value" />'
            },
            {
                label: '自己的碰撞模式',
                shapeClassName: 'reporterBlock',
                parts: [{text: '自己的碰撞模式'}],
                xml: '<block type="scratch3d_sensing_collision_mode" />'
            },
            {
                label: '自己正在显示 ?',
                shapeClassName: 'booleanBlock',
                parts: [{text: '自己正在显示 ?'}],
                xml: '<block type="scratch3d_sensing_visible_is" />'
            },
            {
                label: '对象 任意对象 正在显示 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '对象'},
                    {value: '任意对象', dropdown: true},
                    {text: '正在显示 ?'}
                ],
                xml: '<block type="scratch3d_sensing_object_visible_is" />'
            },
            {
                label: '自己的状态是 普通 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '自己的状态是'},
                    {value: '普通', dropdown: true},
                    {text: '?'}
                ],
                xml: '<block type="scratch3d_sensing_state_is" />'
            },
            {
                label: '对象 任意对象 的状态是 普通 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '对象'},
                    {value: '任意对象', dropdown: true},
                    {text: '的状态是'},
                    {value: '普通', dropdown: true},
                    {text: '?'}
                ],
                xml: '<block type="scratch3d_sensing_object_state_is" />'
            },
            {
                label: '自己的碰撞是 实体阻挡 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '自己的碰撞是'},
                    {value: '实体阻挡', dropdown: true},
                    {text: '?'}
                ],
                xml: '<block type="scratch3d_sensing_collision_is" />'
            },
            {
                label: '对象 任意对象 的碰撞是 实体阻挡 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '对象'},
                    {value: '任意对象', dropdown: true},
                    {text: '的碰撞是'},
                    {value: '实体阻挡', dropdown: true},
                    {text: '?'}
                ],
                xml: '<block type="scratch3d_sensing_object_collision_is" />'
            },
            {
                label: '脚下地形是 草地方块 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '脚下地形是'},
                    {value: '草地方块', dropdown: true},
                    {text: '?'}
                ],
                xml: '<block type="scratch3d_sensing_terrain_under_is" />'
            },
            {
                label: '前方地形是 草地方块 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '前方'},
                    {value: '右上', dropdown: true},
                    {text: '地形是'},
                    {value: '草地方块', dropdown: true},
                    {text: '?'}
                ],
                xml: '<block type="scratch3d_sensing_front_terrain_is" />'
            },
            {
                label: '当前天气是 晴朗 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '当前天气是'},
                    {value: '晴朗', dropdown: true},
                    {text: '?'}
                ],
                xml: '<block type="scratch3d_sensing_weather_is" />'
            }
        ]
    },
    {
        id: 'operators',
        label: '运算',
        blockClassName: 'operatorBlock',
        blocks: [
            {
                label: '0 = 0 ?',
                shapeClassName: 'booleanBlock',
                parts: [
                    {value: '0'},
                    {value: '=', dropdown: true},
                    {value: '0'},
                    {text: '?'}
                ],
                xml: `<block type="scratch3d_operator_compare">
                    ${NUMBER_SHADOW('LEFT', 0)}
                    ${NUMBER_SHADOW('RIGHT', 0)}
                </block>`
            },
            {
                label: '条件 且 条件',
                shapeClassName: 'booleanBlock',
                parts: [
                    {value: '条件'},
                    {text: '且'},
                    {value: '条件'}
                ],
                xml: '<block type="scratch3d_operator_and" />'
            },
            {
                label: '条件 或 条件',
                shapeClassName: 'booleanBlock',
                parts: [
                    {value: '条件'},
                    {text: '或'},
                    {value: '条件'}
                ],
                xml: '<block type="scratch3d_operator_or" />'
            },
            {
                label: '不成立 条件',
                shapeClassName: 'booleanBlock',
                parts: [
                    {text: '不成立'},
                    {value: '条件'}
                ],
                xml: '<block type="scratch3d_operator_not" />'
            },
            {
                label: '0 + 0',
                shapeClassName: 'reporterBlock',
                parts: [
                    {value: '0'},
                    {value: '+', dropdown: true},
                    {value: '0'}
                ],
                xml: `<block type="scratch3d_operator_math">
                    ${NUMBER_SHADOW('LEFT', 0)}
                    ${NUMBER_SHADOW('RIGHT', 0)}
                </block>`
            }
        ]
    },
    {
        id: 'variables',
        label: '变量',
        blockClassName: 'variableBlock',
        blocks: [
            {
                label: '变量 分数',
                shapeClassName: 'reporterBlock',
                parts: [
                    {text: '变量'},
                    {value: '分数', dropdown: true}
                ],
                xml: '<block type="scratch3d_variable_value" />'
            },
            {
                label: '将变量 分数 设为 0',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '将变量'},
                    {value: '分数', dropdown: true},
                    {text: '设为'},
                    {value: '0'}
                ],
                xml: `<block type="scratch3d_variable_set">${NUMBER_SHADOW('VALUE', 0)}</block>`
            },
            {
                label: '将变量 分数 增加 1',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '将变量'},
                    {value: '分数', dropdown: true},
                    {text: '增加'},
                    {value: '1'}
                ],
                xml: `<block type="scratch3d_variable_change">${NUMBER_SHADOW('VALUE', 1)}</block>`
            }
        ]
    },
    {
        id: 'scene',
        label: '场景',
        blockClassName: 'sceneBlock',
        blocks: [
            {
                label: '设置天气为 晴朗',
                shapeClassName: 'statementBlock',
                parts: [
                    {text: '设置天气为'},
                    {value: '晴朗', dropdown: true}
                ],
                xml: '<block type="scratch3d_scene_set_weather" />'
            }
        ]
    }
];

const register3DBlocks = ScratchBlocks => {
    if (ScratchBlocks.Blocks.scratch3d_motion_move_toward_object) {
        return;
    }

    ScratchBlocks.Blocks.scratch3d_event_when_start = {
        init: function () {
            this.jsonInit({
                message0: '当开始运行',
                extensions: ['colours_event', 'shape_hat']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_event_when_click_self = {
        init: function () {
            this.jsonInit({
                message0: '当点击自己',
                extensions: ['colours_event', 'shape_hat']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_event_when_receive_message = {
        init: function () {
            this.jsonInit({
                message0: '当收到消息 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'MESSAGE',
                        options: getScratch3DMessageMenuOptions
                    }
                ],
                extensions: ['colours_event', 'shape_hat']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_event_broadcast_message = {
        init: function () {
            this.jsonInit({
                message0: '广播消息 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'MESSAGE',
                        options: getScratch3DMessageMenuOptions
                    }
                ],
                extensions: ['colours_event', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_event_send_message_to_object = {
        init: function () {
            this.jsonInit({
                message0: '发送消息 %1 给 %2',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'MESSAGE',
                        options: getScratch3DMessageMenuOptions
                    },
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    }
                ],
                extensions: ['colours_event', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_event_when_touching_object = {
        init: function () {
            this.jsonInit({
                message0: '当碰到 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    }
                ],
                extensions: ['colours_event', 'shape_hat']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_face_direction = {
        init: function () {
            this.jsonInit({
                message0: '面向 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'DIRECTION',
                        options: DIRECTION_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_move_forward = {
        init: function () {
            this.jsonInit({
                message0: '向前移动 %1 格',
                args0: [
                    {
                        type: 'input_value',
                        name: 'STEPS'
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_face_object = {
        init: function () {
            this.jsonInit({
                message0: '面向对象 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_move_grid = {
        init: function () {
            this.jsonInit({
                message0: '让自己向 %1 移动 %2 格',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'DIRECTION',
                        options: DIRECTION_MENU_OPTIONS
                    },
                    {
                        type: 'input_value',
                        name: 'STEPS'
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_move_to = {
        init: function () {
            this.jsonInit({
                message0: '移动到格子 x %1 y %2 z %3',
                args0: [
                    {
                        type: 'input_value',
                        name: 'X'
                    },
                    {
                        type: 'input_value',
                        name: 'Y'
                    },
                    {
                        type: 'input_value',
                        name: 'Z'
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_change_position = {
        init: function () {
            this.jsonInit({
                message0: '将 %1 坐标增加 %2',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'AXIS',
                        options: AXIS_MENU_OPTIONS
                    },
                    {
                        type: 'input_value',
                        name: 'VALUE'
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_set_position_axis = {
        init: function () {
            this.jsonInit({
                message0: '将 %1 坐标设为 %2',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'AXIS',
                        options: AXIS_MENU_OPTIONS
                    },
                    {
                        type: 'input_value',
                        name: 'VALUE'
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_move_to_object = {
        init: function () {
            this.jsonInit({
                message0: '移动到对象位置 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_move_toward_object = {
        init: function () {
            this.jsonInit({
                message0: '靠近对象 %1 %2 格',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    },
                    {
                        type: 'input_value',
                        name: 'STEPS'
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_move_away_object = {
        init: function () {
            this.jsonInit({
                message0: '远离对象 %1 %2 格',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    },
                    {
                        type: 'input_value',
                        name: 'STEPS'
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_change_z = {
        init: function () {
            this.jsonInit({
                message0: '将高度增加 %1',
                args0: [
                    {
                        type: 'input_value',
                        name: 'Z'
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_turn_axis = {
        init: function () {
            this.jsonInit({
                message0: '绕 %1 轴旋转 %2 度',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'AXIS',
                        options: AXIS_MENU_OPTIONS
                    },
                    {
                        type: 'input_value',
                        name: 'DEGREES'
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_set_rotation_axis = {
        init: function () {
            this.jsonInit({
                message0: '将 %1 轴角度设为 %2',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'AXIS',
                        options: AXIS_MENU_OPTIONS
                    },
                    {
                        type: 'input_value',
                        name: 'DEGREES'
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_jump = {
        init: function () {
            this.jsonInit({
                message0: '让自己跳起 %1 格',
                args0: [
                    {
                        type: 'input_value',
                        name: 'HEIGHT'
                    }
                ],
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_motion_snap_to_surface = {
        init: function () {
            this.jsonInit({
                message0: '贴合当前地面',
                extensions: ['colours_motion', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_control_wait = {
        init: function () {
            this.jsonInit({
                message0: '等待 %1 秒',
                args0: [
                    {
                        type: 'input_value',
                        name: 'SECONDS'
                    }
                ],
                extensions: ['colours_control', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_control_wait_until = {
        init: function () {
            this.jsonInit({
                message0: '等待直到 %1',
                args0: [
                    {
                        type: 'input_value',
                        name: 'CONDITION'
                    }
                ],
                extensions: ['colours_control', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_control_repeat = {
        init: function () {
            this.jsonInit({
                message0: '重复执行 %1 次',
                args0: [
                    {
                        type: 'input_value',
                        name: 'TIMES'
                    }
                ],
                message1: '%1',
                args1: [
                    {
                        type: 'input_statement',
                        name: 'SUBSTACK'
                    }
                ],
                extensions: ['colours_control', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_control_repeat_until = {
        init: function () {
            this.jsonInit({
                message0: '重复直到 %1',
                args0: [
                    {
                        type: 'input_value',
                        name: 'CONDITION'
                    }
                ],
                message1: '%1',
                args1: [
                    {
                        type: 'input_statement',
                        name: 'SUBSTACK'
                    }
                ],
                extensions: ['colours_control', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_control_if = {
        init: function () {
            this.jsonInit({
                message0: '如果 %1 那么',
                args0: [
                    {
                        type: 'input_value',
                        name: 'CONDITION'
                    }
                ],
                message1: '%1',
                args1: [
                    {
                        type: 'input_statement',
                        name: 'SUBSTACK'
                    }
                ],
                extensions: ['colours_control', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_control_if_else = {
        init: function () {
            this.jsonInit({
                message0: '如果 %1 那么',
                args0: [
                    {
                        type: 'input_value',
                        name: 'CONDITION'
                    }
                ],
                message1: '%1',
                args1: [
                    {
                        type: 'input_statement',
                        name: 'SUBSTACK'
                    }
                ],
                message2: '否则 %1',
                args2: [
                    {
                        type: 'input_statement',
                        name: 'SUBSTACK2'
                    }
                ],
                extensions: ['colours_control', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_control_forever = {
        init: function () {
            this.jsonInit({
                message0: '一直重复',
                message1: '%1',
                args1: [
                    {
                        type: 'input_statement',
                        name: 'SUBSTACK'
                    }
                ],
                extensions: ['colours_control', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_control_stop_script = {
        init: function () {
            this.jsonInit({
                message0: '停止此脚本',
                extensions: ['colours_control', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_looks_play_animation = {
        init: function () {
            this.jsonInit({
                message0: '播放动画 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'ANIMATION',
                        options: ANIMATION_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_looks', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_looks_wait_animation_done = {
        init: function () {
            this.jsonInit({
                message0: '等待动画播放完成',
                extensions: ['colours_looks', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_looks_stop_animation = {
        init: function () {
            this.jsonInit({
                message0: '停止动画',
                extensions: ['colours_looks', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_looks_set_state = {
        init: function () {
            this.jsonInit({
                message0: '将自己的状态设为 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'STATE',
                        options: STATE_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_looks', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_looks_set_collision = {
        init: function () {
            this.jsonInit({
                message0: '将碰撞设为 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'COLLISION',
                        options: COLLISION_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_looks', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_looks_set_size = {
        init: function () {
            this.jsonInit({
                message0: '将大小设为 %1 百分比',
                args0: [
                    {
                        type: 'input_value',
                        name: 'SIZE'
                    }
                ],
                extensions: ['colours_looks', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_looks_change_size = {
        init: function () {
            this.jsonInit({
                message0: '将大小增加 %1',
                args0: [
                    {
                        type: 'input_value',
                        name: 'CHANGE'
                    }
                ],
                extensions: ['colours_looks', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_looks_size_value = {
        init: function () {
            this.jsonInit({
                message0: '大小',
                extensions: ['colours_looks', 'output_number']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_looks_show = {
        init: function () {
            this.jsonInit({
                message0: '显示自己',
                extensions: ['colours_looks', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_looks_hide = {
        init: function () {
            this.jsonInit({
                message0: '隐藏自己',
                extensions: ['colours_looks', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_touching_object = {
        init: function () {
            this.jsonInit({
                message0: '碰到对象 %1 ?',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    }
                ],
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_front_blocked = {
        init: function () {
            this.jsonInit({
                message0: '前方 %1 有障碍 ?',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'DIRECTION',
                        options: DIRECTION_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_distance_less_than = {
        init: function () {
            this.jsonInit({
                message0: '到对象 %1 的距离 < %2 ?',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    },
                    {
                        type: 'input_value',
                        name: 'DISTANCE'
                    }
                ],
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_distance_to = {
        init: function () {
            this.jsonInit({
                message0: '到对象 %1 的距离',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    }
                ],
                extensions: ['colours_sensing', 'output_number']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_position_value = {
        init: function () {
            this.jsonInit({
                message0: '%1 的 %2 坐标',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    },
                    {
                        type: 'field_dropdown',
                        name: 'AXIS',
                        options: AXIS_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_sensing', 'output_number']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_rotation_value = {
        init: function () {
            this.jsonInit({
                message0: '%1 的 %2 轴角度',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    },
                    {
                        type: 'field_dropdown',
                        name: 'AXIS',
                        options: AXIS_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_sensing', 'output_number']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_weather_value = {
        init: function () {
            this.jsonInit({
                message0: '当前天气',
                extensions: ['colours_sensing', 'output_string']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_weather_property = {
        init: function () {
            this.jsonInit({
                message0: '天气属性 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'PROPERTY',
                        options: WEATHER_PROPERTY_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_sensing', 'output_string']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_terrain_under = {
        init: function () {
            this.jsonInit({
                message0: '脚下地形',
                extensions: ['colours_sensing', 'output_string']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_surface_z = {
        init: function () {
            this.jsonInit({
                message0: '当前地面高度',
                extensions: ['colours_sensing', 'output_number']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_state_value = {
        init: function () {
            this.jsonInit({
                message0: '自己的状态',
                extensions: ['colours_sensing', 'output_string']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_collision_mode = {
        init: function () {
            this.jsonInit({
                message0: '自己的碰撞模式',
                extensions: ['colours_sensing', 'output_string']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_visible_is = {
        init: function () {
            this.jsonInit({
                message0: '自己正在显示 ?',
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_object_visible_is = {
        init: function () {
            this.jsonInit({
                message0: '对象 %1 正在显示 ?',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    }
                ],
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_state_is = {
        init: function () {
            this.jsonInit({
                message0: '自己的状态是 %1 ?',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'STATE',
                        options: STATE_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_object_state_is = {
        init: function () {
            this.jsonInit({
                message0: '对象 %1 的状态是 %2 ?',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    },
                    {
                        type: 'field_dropdown',
                        name: 'STATE',
                        options: STATE_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_terrain_under_is = {
        init: function () {
            this.jsonInit({
                message0: '脚下地形是 %1 ?',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'TERRAIN',
                        options: TERRAIN_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_front_terrain_is = {
        init: function () {
            this.jsonInit({
                message0: '前方 %1 地形是 %2 ?',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'DIRECTION',
                        options: DIRECTION_MENU_OPTIONS
                    },
                    {
                        type: 'field_dropdown',
                        name: 'TERRAIN',
                        options: TERRAIN_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_collision_is = {
        init: function () {
            this.jsonInit({
                message0: '自己的碰撞是 %1 ?',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'COLLISION',
                        options: COLLISION_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_object_collision_is = {
        init: function () {
            this.jsonInit({
                message0: '对象 %1 的碰撞是 %2 ?',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OBJECT',
                        options: getScratch3DObjectMenuOptions
                    },
                    {
                        type: 'field_dropdown',
                        name: 'COLLISION',
                        options: COLLISION_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_sensing_weather_is = {
        init: function () {
            this.jsonInit({
                message0: '当前天气是 %1 ?',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'WEATHER',
                        options: WEATHER_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_sensing', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_operator_compare = {
        init: function () {
            this.jsonInit({
                message0: '%1 %2 %3',
                args0: [
                    {
                        type: 'input_value',
                        name: 'LEFT'
                    },
                    {
                        type: 'field_dropdown',
                        name: 'OPERATOR',
                        options: COMPARE_MENU_OPTIONS
                    },
                    {
                        type: 'input_value',
                        name: 'RIGHT'
                    }
                ],
                extensions: ['colours_operators', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_operator_and = {
        init: function () {
            this.jsonInit({
                message0: '%1 且 %2',
                args0: [
                    {
                        type: 'input_value',
                        name: 'LEFT'
                    },
                    {
                        type: 'input_value',
                        name: 'RIGHT'
                    }
                ],
                extensions: ['colours_operators', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_operator_or = {
        init: function () {
            this.jsonInit({
                message0: '%1 或 %2',
                args0: [
                    {
                        type: 'input_value',
                        name: 'LEFT'
                    },
                    {
                        type: 'input_value',
                        name: 'RIGHT'
                    }
                ],
                extensions: ['colours_operators', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_operator_not = {
        init: function () {
            this.jsonInit({
                message0: '不成立 %1',
                args0: [
                    {
                        type: 'input_value',
                        name: 'CONDITION'
                    }
                ],
                extensions: ['colours_operators', 'output_boolean']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_operator_math = {
        init: function () {
            this.jsonInit({
                message0: '%1 %2 %3',
                args0: [
                    {
                        type: 'input_value',
                        name: 'LEFT'
                    },
                    {
                        type: 'field_dropdown',
                        name: 'OPERATOR',
                        options: MATH_MENU_OPTIONS
                    },
                    {
                        type: 'input_value',
                        name: 'RIGHT'
                    }
                ],
                extensions: ['colours_operators', 'output_number']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_variable_value = {
        init: function () {
            this.jsonInit({
                message0: '变量 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'VARIABLE',
                        options: getScratch3DVariableMenuOptions
                    }
                ],
                extensions: ['colours_data', 'output_number']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_variable_set = {
        init: function () {
            this.jsonInit({
                message0: '将变量 %1 设为 %2',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'VARIABLE',
                        options: getScratch3DVariableMenuOptions
                    },
                    {
                        type: 'input_value',
                        name: 'VALUE'
                    }
                ],
                extensions: ['colours_data', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_variable_change = {
        init: function () {
            this.jsonInit({
                message0: '将变量 %1 增加 %2',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'VARIABLE',
                        options: getScratch3DVariableMenuOptions
                    },
                    {
                        type: 'input_value',
                        name: 'VALUE'
                    }
                ],
                extensions: ['colours_data', 'shape_statement']
            });
        }
    };

    ScratchBlocks.Blocks.scratch3d_scene_set_weather = {
        init: function () {
            this.jsonInit({
                message0: '设置天气为 %1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'WEATHER',
                        options: WEATHER_MENU_OPTIONS
                    }
                ],
                extensions: ['colours_operators', 'shape_statement']
            });
        }
    };
};

class Stage3DBlockWorkspace extends React.PureComponent {
    constructor (props) {
        super(props);
        this.ScratchBlocks = VMScratchBlocks(props.vm);
        this.workspace = null;
        this.isLoadingWorkspace = false;
        this.isUpdatingExternalBlockPreview = false;
        this.externalBlockPreview = null;
        this.externalBlockPreviewXml = null;
        this.externalBlockPreviewConnection = null;
        this.renderedObjectId = null;
        this.renderedXml = null;
        this.centerWorkspaceTimer = null;
        this.setWorkspaceNode = this.setWorkspaceNode.bind(this);
        this.handleWorkspaceChange = this.handleWorkspaceChange.bind(this);
        this.resizeWorkspace = this.resizeWorkspace.bind(this);
        this.scheduleCenterWorkspace = this.scheduleCenterWorkspace.bind(this);
        this.centerWorkspaceBlocks = this.centerWorkspaceBlocks.bind(this);
    }

    componentDidMount () {
        setScratch3DObjectMenuOptions(this.props.objectMenuOptions);
        setScratch3DVariableMenuOptions(this.props.variableMenuOptions);
        setScratch3DMessageMenuOptions(this.props.messageMenuOptions);
        ensureScratch3DContextMenuMessages(this.ScratchBlocks);
        register3DBlocks(this.ScratchBlocks);

        const workspaceConfig = defaultsDeep({}, {
            media: this.props.media,
            toolbox: this.props.showToolbox ? THREE_D_TOOLBOX_XML : null,
            zoom: {
                controls: true,
                wheel: true,
                pinch: true,
                startScale: 0.72
            },
            move: {
                wheel: true
            },
            grid: {
                spacing: 40,
                length: 2,
                colour: '#d8dee9'
            },
            comments: true,
            collapse: false,
            trashcan: true,
            toolboxPosition: 'end',
            sounds: false,
            modalInputs: false,
            rtl: false,
            theme: new this.ScratchBlocks.Theme(
                DEFAULT_MODE,
                getColorsForMode(DEFAULT_MODE)
            ),
            scratchTheme: 'classic'
        });

        this.workspace = this.ScratchBlocks.inject(this.workspaceNode, workspaceConfig);
        this.workspace.addChangeListener(this.handleWorkspaceChange);
        this.loadWorkspace(this.props.workspaceXml, this.props.selectedObjectId);
        window.addEventListener('resize', this.resizeWorkspace);
        window.setTimeout(this.resizeWorkspace, 0);
    }

    componentDidUpdate (previousProps) {
        if (previousProps.objectMenuOptions !== this.props.objectMenuOptions) {
            setScratch3DObjectMenuOptions(this.props.objectMenuOptions);
        }
        if (previousProps.variableMenuOptions !== this.props.variableMenuOptions) {
            setScratch3DVariableMenuOptions(this.props.variableMenuOptions);
        }
        if (previousProps.messageMenuOptions !== this.props.messageMenuOptions) {
            setScratch3DMessageMenuOptions(this.props.messageMenuOptions);
        }
        if (
            previousProps.selectedObjectId !== this.props.selectedObjectId ||
            this.props.workspaceXml !== this.renderedXml
        ) {
            const selectedObjectChanged = previousProps.selectedObjectId !== this.props.selectedObjectId;
            this.loadWorkspace(
                this.props.workspaceXml,
                this.props.selectedObjectId,
                selectedObjectChanged
            );
        }
        this.resizeWorkspace();
    }

    componentWillUnmount () {
        window.removeEventListener('resize', this.resizeWorkspace);
        this.cancelCenterWorkspace();
        this.clearExternalBlockPreview();
        if (this.workspace) {
            this.workspace.removeChangeListener(this.handleWorkspaceChange);
            this.workspace.dispose();
            this.workspace = null;
        }
    }

    setWorkspaceNode (node) {
        this.workspaceNode = node;
    }

    loadWorkspace (workspaceXml, selectedObjectId, shouldCenterWorkspace = true) {
        if (!this.workspace) return;

        this.clearExternalBlockPreview();
        const xmlText = workspaceXml || EMPTY_WORKSPACE_XML;
        this.isLoadingWorkspace = true;
        try {
            this.ScratchBlocks.Events.disable();
            const dom = this.ScratchBlocks.utils.xml.textToDom(xmlText);
            this.ScratchBlocks.clearWorkspaceAndLoadFromXml(dom, this.workspace);
            this.workspace.clearUndo();
            this.renderedObjectId = selectedObjectId;
            this.renderedXml = xmlText;
        } catch (error) {
            // Keep the editor usable if a saved experimental block XML is incomplete.
            // eslint-disable-next-line no-console
            console.warn('Failed to load 3D object block workspace', error);
        } finally {
            this.ScratchBlocks.Events.enable();
            this.isLoadingWorkspace = false;
            this.resizeWorkspace();
            if (shouldCenterWorkspace && workspaceXmlHasBlocks(xmlText)) {
                this.scheduleCenterWorkspace();
            } else {
                this.cancelCenterWorkspace();
            }
        }
    }

    handleWorkspaceChange (event) {
        if (
            !this.workspace ||
            this.isLoadingWorkspace ||
            this.isUpdatingExternalBlockPreview ||
            (event && event.isUiEvent)
        ) {
            return;
        }

        const xmlDom = this.ScratchBlocks.Xml.workspaceToDom(this.workspace);
        const xmlText = this.ScratchBlocks.Xml.domToText(xmlDom);
        this.renderedXml = xmlText;
        this.props.onChange(xmlText);
    }

    resizeWorkspace () {
        if (!this.workspace || !this.ScratchBlocks.svgResize) return;
        this.ScratchBlocks.svgResize(this.workspace);
    }

    scheduleCenterWorkspace () {
        this.cancelCenterWorkspace();

        this.centerWorkspaceTimer = window.setTimeout(() => {
            this.centerWorkspaceTimer = null;
            this.centerWorkspaceBlocks();
        }, 0);
    }

    cancelCenterWorkspace () {
        if (!this.centerWorkspaceTimer) return;
        window.clearTimeout(this.centerWorkspaceTimer);
        this.centerWorkspaceTimer = null;
    }

    centerWorkspaceBlocks () {
        if (!this.workspace) return;

        this.resizeWorkspace();

        const zoomOptions = this.workspace.options && this.workspace.options.zoomOptions;
        const targetScale = zoomOptions && zoomOptions.startScale;
        const currentScale = this.workspace.scale;
        const scaleSpeed = zoomOptions && zoomOptions.scaleSpeed;

        if (
            typeof this.workspace.zoomCenter === 'function' &&
            Number.isFinite(targetScale) &&
            Number.isFinite(currentScale) &&
            Number.isFinite(scaleSpeed) &&
            currentScale > 0 &&
            scaleSpeed > 0 &&
            scaleSpeed !== 1
        ) {
            const amount = Math.log(targetScale / currentScale) / Math.log(scaleSpeed);
            if (Number.isFinite(amount) && Math.abs(amount) > 0.0001) {
                this.workspace.zoomCenter(amount);
            }
        }

        if (typeof this.workspace.scrollCenter === 'function') {
            this.workspace.scrollCenter();
        }
    }

    containsClientPoint (clientX, clientY) {
        if (!this.workspaceNode) return false;
        const rect = this.workspaceNode.getBoundingClientRect();
        return clientX >= rect.left &&
            clientX <= rect.right &&
            clientY >= rect.top &&
            clientY <= rect.bottom;
    }

    getInsertPosition (dropPosition, fallbackIndex) {
        const scale = this.workspace.scale || 1;

        if (
            dropPosition &&
            Number.isFinite(dropPosition.clientX) &&
            Number.isFinite(dropPosition.clientY) &&
            this.workspaceNode
        ) {
            const rect = this.workspaceNode.getBoundingClientRect();
            return {
                x: (-this.workspace.scrollX + (dropPosition.clientX - rect.left)) / scale,
                y: (-this.workspace.scrollY + (dropPosition.clientY - rect.top)) / scale
            };
        }

        return {
            x: (-this.workspace.scrollX + 48) / scale,
            y: (-this.workspace.scrollY + 48 + (fallbackIndex * 28)) / scale
        };
    }

    getBlockConnections (block) {
        if (!block) return [];
        if (typeof block.getConnections_ === 'function') {
            return block.getConnections_(false);
        }
        return [
            block.outputConnection,
            block.previousConnection,
            block.nextConnection,
            ...(block.inputList || []).map(input => input.connection)
        ].filter(Boolean);
    }

    getConnectionSnapRadius () {
        return Math.max(
            INSERT_SNAP_RADIUS,
            (this.ScratchBlocks.config && this.ScratchBlocks.config.connectingSnapRadius) || 0
        );
    }

    getSuperiorConnection (firstConnection, secondConnection) {
        if (
            firstConnection &&
            typeof firstConnection.isSuperior === 'function' &&
            firstConnection.isSuperior()
        ) {
            return firstConnection;
        }

        if (
            secondConnection &&
            typeof secondConnection.isSuperior === 'function' &&
            secondConnection.isSuperior()
        ) {
            return secondConnection;
        }

        return firstConnection || secondConnection;
    }

    renderConnectedStack (localConnection, neighbourConnection, insertedBlock) {
        const superiorConnection = this.getSuperiorConnection(localConnection, neighbourConnection);
        const superiorBlock = superiorConnection && superiorConnection.getSourceBlock &&
            superiorConnection.getSourceBlock();
        const rootBlock = superiorBlock && superiorBlock.getRootBlock ?
            superiorBlock.getRootBlock() :
            superiorBlock;

        if (rootBlock && rootBlock.render) {
            rootBlock.render();
        } else if (insertedBlock && insertedBlock.render) {
            insertedBlock.render();
        }

        if (superiorConnection && superiorConnection.tightenEfficiently) {
            superiorConnection.tightenEfficiently();
        }
    }

    findNearbyConnectionCandidate (block) {
        if (!block) return null;

        const radius = this.getConnectionSnapRadius();
        const rootBlock = block.getRootBlock ? block.getRootBlock() : block;
        let candidate = null;

        this.getBlockConnections(block).forEach(localConnection => {
            if (
                !localConnection ||
                (localConnection.isConnected && localConnection.isConnected()) ||
                typeof localConnection.closest !== 'function'
            ) {
                return;
            }

            const dxy = {x: 0, y: 0};
            const result = localConnection.closest(radius, dxy);
            const neighbourConnection = result && result.connection;
            if (!neighbourConnection) return;

            const neighbourBlock = neighbourConnection.getSourceBlock &&
                neighbourConnection.getSourceBlock();
            const neighbourRoot = neighbourBlock && neighbourBlock.getRootBlock ?
                neighbourBlock.getRootBlock() :
                neighbourBlock;
            const neighbourIsConnected = neighbourConnection.isConnected &&
                neighbourConnection.isConnected();
            const neighbourIsSuperior = neighbourConnection.isSuperior &&
                neighbourConnection.isSuperior();
            if (
                !neighbourBlock ||
                neighbourRoot === rootBlock ||
                (neighbourIsConnected && !neighbourIsSuperior)
            ) {
                return;
            }

            if (!candidate || result.radius < candidate.radius) {
                candidate = {
                    localConnection,
                    neighbourConnection,
                    radius: result.radius
                };
            }
        });

        return candidate;
    }

    connectInsertedBlockNearStack (block) {
        const candidate = this.findNearbyConnectionCandidate(block);
        if (!candidate) return false;

        if (!candidate.localConnection.connect(candidate.neighbourConnection)) return false;

        this.renderConnectedStack(
            candidate.localConnection,
            candidate.neighbourConnection,
            block
        );

        if (block.bumpNeighbours) {
            block.bumpNeighbours();
        }
        return true;
    }

    createExternalBlockPreview (blockXml) {
        if (!this.workspace || !blockXml) return null;
        if (this.externalBlockPreview && this.externalBlockPreviewXml === blockXml) {
            return this.externalBlockPreview;
        }

        this.clearExternalBlockPreview();
        this.isUpdatingExternalBlockPreview = true;

        try {
            this.ScratchBlocks.Events.disable();
            const xml = this.ScratchBlocks.utils.xml.textToDom(`<xml>${blockXml}</xml>`);
            const blockDom = xml.firstElementChild;
            if (!blockDom) return null;

            const block = this.ScratchBlocks.Xml.domToBlock(blockDom, this.workspace);
            const svgRoot = block && block.getSvgRoot && block.getSvgRoot();
            if (svgRoot && svgRoot.classList) {
                svgRoot.classList.add('scratch3d-external-block-preview');
            }
            if (block && block.setMovable) block.setMovable(false);
            if (block && block.setDeletable) block.setDeletable(false);
            if (block && block.setEditable) block.setEditable(false);

            this.externalBlockPreview = block;
            this.externalBlockPreviewXml = blockXml;
            return block;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Failed to create 3D block drag preview', error);
            return null;
        } finally {
            this.ScratchBlocks.Events.enable();
            this.isUpdatingExternalBlockPreview = false;
        }
    }

    clearExternalBlockPreviewHighlight () {
        if (
            this.externalBlockPreviewConnection &&
            this.externalBlockPreviewConnection.unhighlight
        ) {
            this.externalBlockPreviewConnection.unhighlight();
        }
        this.externalBlockPreviewConnection = null;
    }

    clearExternalBlockPreview () {
        this.clearExternalBlockPreviewHighlight();

        if (!this.externalBlockPreview) return;
        const block = this.externalBlockPreview;
        this.externalBlockPreview = null;
        this.externalBlockPreviewXml = null;
        this.isUpdatingExternalBlockPreview = true;

        try {
            this.ScratchBlocks.Events.disable();
            if (block.dispose) {
                block.dispose(false, false);
            }
        } finally {
            this.ScratchBlocks.Events.enable();
            this.isUpdatingExternalBlockPreview = false;
        }
    }

    updateExternalBlockPreview (blockXml, dropPosition) {
        if (!this.workspace || !blockXml || !dropPosition) {
            this.clearExternalBlockPreview();
            return;
        }

        this.cancelCenterWorkspace();

        const block = this.createExternalBlockPreview(blockXml);
        if (!block || !block.moveBy) return;

        this.isUpdatingExternalBlockPreview = true;
        try {
            this.ScratchBlocks.Events.disable();
            const existingBlocks = this.workspace.getTopBlocks(false)
                .filter(existingBlock => existingBlock !== block);
            const target = this.getInsertPosition(dropPosition, existingBlocks.length);
            const current = block.getRelativeToSurfaceXY ?
                block.getRelativeToSurfaceXY() :
                {x: 0, y: 0};
            block.moveBy(target.x - current.x, target.y - current.y);

            const candidate = this.findNearbyConnectionCandidate(block);
            const svgRoot = block.getSvgRoot && block.getSvgRoot();
            if (candidate) {
                const snapX = candidate.neighbourConnection.x - candidate.localConnection.x;
                const snapY = candidate.neighbourConnection.y - candidate.localConnection.y;
                if (Number.isFinite(snapX) && Number.isFinite(snapY)) {
                    block.moveBy(snapX, snapY);
                }

                if (this.externalBlockPreviewConnection !== candidate.neighbourConnection) {
                    this.clearExternalBlockPreviewHighlight();
                    if (candidate.neighbourConnection.highlight) {
                        candidate.neighbourConnection.highlight();
                    }
                    this.externalBlockPreviewConnection = candidate.neighbourConnection;
                }
                if (svgRoot && svgRoot.classList) {
                    svgRoot.classList.add('scratch3d-external-block-preview-snapped');
                }
            } else {
                this.clearExternalBlockPreviewHighlight();
                if (svgRoot && svgRoot.classList) {
                    svgRoot.classList.remove('scratch3d-external-block-preview-snapped');
                }
            }
        } finally {
            this.ScratchBlocks.Events.enable();
            this.isUpdatingExternalBlockPreview = false;
        }
    }

    insertBlock (blockXml, dropPosition) {
        if (!this.workspace || !blockXml) return;

        this.cancelCenterWorkspace();

        try {
            const xml = this.ScratchBlocks.utils.xml.textToDom(`<xml>${blockXml}</xml>`);
            const blockDom = xml.firstElementChild;
            if (!blockDom) return;

            const existingBlocks = this.workspace.getTopBlocks(false);
            const block = this.ScratchBlocks.Xml.domToBlock(blockDom, this.workspace);
            if (block && block.moveBy) {
                const target = this.getInsertPosition(dropPosition, existingBlocks.length);
                const current = block.getRelativeToSurfaceXY ? block.getRelativeToSurfaceXY() : {x: 0, y: 0};
                block.moveBy(target.x - current.x, target.y - current.y);
            }
            this.connectInsertedBlockNearStack(block);
            if (block && block.select) {
                block.select();
            }
            this.handleWorkspaceChange();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Failed to insert 3D block', error);
        }
    }

    render () {
        return (
            <div className={styles.threeDBlockEditor}>
                <div className={styles.threeDBlockHeader}>
                    <div className={styles.threeDBlockObjectName}>{this.props.selectedObjectName}</div>
                </div>
                <div
                    className={styles.threeDBlockWorkspace}
                    ref={this.setWorkspaceNode}
                />
            </div>
        );
    }
}

Stage3DBlockWorkspace.propTypes = {
    onChange: PropTypes.func.isRequired,
    media: PropTypes.string,
    messageMenuOptions: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    objectMenuOptions: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    selectedObjectId: PropTypes.string,
    selectedObjectName: PropTypes.string,
    showToolbox: PropTypes.bool,
    variableMenuOptions: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    vm: PropTypes.shape({}).isRequired,
    workspaceXml: PropTypes.string
};

Stage3DBlockWorkspace.defaultProps = {
    media: 'static/blocks-media/default/',
    messageMenuOptions: MESSAGE_MENU_OPTIONS,
    objectMenuOptions: DEFAULT_OBJECT_MENU_OPTIONS,
    selectedObjectId: null,
    selectedObjectName: '',
    showToolbox: true,
    variableMenuOptions: VARIABLE_MENU_OPTIONS,
    workspaceXml: EMPTY_WORKSPACE_XML
};

export default Stage3DBlockWorkspace;
